import { useDispatch } from 'react-redux';
import { ActionTypeConflictProposal } from '../../api/gms/proposal/proposal-api';
import ConflictProposalForApprovalListTBody from '../../components/module/conflict-proposal-for-approval/conflict-proposal-for-approval-list-tbody';
import Pagination from '../../components/ui/pagination';
import ProposalTHead from '../../components/ui/proposal-thead';
import SearchFilterBar from '../../components/ui/search-filter-bar';
import SectionHeader from '../../components/ui/section-header'
import { proposalHeaderData } from '../../constant/gms-constant'
import { useConflictProposalActions } from '../../hooks/actions/use-conflict-proposal-actions';
import { useConflictProposal } from '../../hooks/proposal/use-conflict-proposal';
import { clearApproveRejectConflictProposal, setApproveRejectConflictProposal } from '../../features/conflict-proposal/conflict-proposal-slice';
import { useConflictProposalDataState } from '../../hooks/data-state/use-conflict-proposal-data-state';
import ConfirmDefaultModal from '../../components/global/modal/confirm-default-modal';
import ProposalDetails from '../../components/module/proposal-status/modal/proposal-details';
import { useRequestExistingGroupListSocket } from '../../hooks/socket/use-request-existing-group-proposal';

export default function ConflictProposalApproval() {

    const dispatch = useDispatch()
    const {
        normalizedOptions,
        errorMessage,
        searchType,
        setSearchType,
        searchValue,
        setSearchValue,
        setPage,
        pagedData,
        loading,
        isErrorGetConflictProposalForApproval,
        handleReload,
        getConflictProposalForApproval,
        defaultTotalPages,
        proposalDefaultPageSize,
        handleChangePageSize,
    } = useConflictProposal();
    useRequestExistingGroupListSocket()
    const {
        handleApprovedRejectConflictProposal,
        isLoadingApproveRejectProposalConflict,
    } = useConflictProposalActions()
    const {
        isShowConfirmationModal,
        conflictProposalId,
        actionType,
        isApproveConflictProposal,
        isShowViewConflictModal,
    } = useConflictProposalDataState()


    const tHeads:string[] = [
        proposalHeaderData.groupName.label,
        proposalHeaderData.proposalType.label,
        proposalHeaderData.product.label,
        proposalHeaderData.basicPlan.label,
        proposalHeaderData.preparedBy.label,
        proposalHeaderData.conflictProposalAction.label,
    ]

    const handleApprove = (value: number, userID: number, status: string, type: ActionTypeConflictProposal) => {
        dispatch(setApproveRejectConflictProposal({
            isShowModal: 'actions',
            actionType: type,
            conflictProposalId: value,
        }))
    }

    const handleReject = (value: number, userID: number, status: string, type: ActionTypeConflictProposal) => {
        dispatch(setApproveRejectConflictProposal({
            isShowModal: 'actions',
            actionType: type,
            conflictProposalId: value,
        }))
    }

    const handleView = (value: number, userID: number, status: string, type: null | ActionTypeConflictProposal) => {
        dispatch(setApproveRejectConflictProposal({
            isShowModal: 'views',
            actionType: null,
            conflictProposalId: value,
        }))
    }

    return (
        <div className="flex h-full min-h-0 w-full flex-col bg-[#f8fafc] rounded-xl border border-gray-200">
            <div className="flex min-h-0 flex-1 flex-col overflow-hidden">
                <SectionHeader
                    title='Existing Group Proposal Approval'
                    subtitle='Review proposals created for existing groups that are requesting coverage under a different product. '
                    icon={
                        <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" className="h-4 w-4">
                            <path d="M13 3H6a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V10z" />
                            <path d="M13 3v7h7" />
                            <path d="M12 13v6m-3-3h6" />
                        </svg>
                    }
                />
                <div className="mx-auto flex min-h-0 w-full flex-1 flex-col gap-2">
                    <div className="flex min-h-0 flex-1 flex-col overflow-hidden bg-white shadow-sm">
                        <div className="shrink-0 border-b border-gray-200 p-3">
                            <SearchFilterBar
                                searchType={searchType}
                                setSearchType={setSearchType}
                                searchValue={searchValue}
                                setSearchValue={setSearchValue}
                                options={normalizedOptions}
                            />
                        </div>
                        {getConflictProposalForApproval?.pagination && (
                            <div className="shrink-0 border-b border-gray-200 bg-gray-50/60 p-3">
                                <Pagination
                                    setPage={setPage}
                                    handleReload={handleReload}
                                    pagination={getConflictProposalForApproval.pagination}
                                    defaultTotalPages={defaultTotalPages}
                                    setDefaultTotalPages={handleChangePageSize}
                                    totalPagesOptions={proposalDefaultPageSize}
                                />
                            </div>
                        )}
                        <div className="min-h-0 flex-1 overflow-auto">
                            <table className="min-w-max w-full border-collapse">
                                <ProposalTHead headers={tHeads}/>
                                <ConflictProposalForApprovalListTBody
                                    headers={tHeads}
                                    typeOfIcon="update"
                                    isLoadingGetProposal={loading}
                                    isErrorGetProposal={isErrorGetConflictProposalForApproval}
                                    errorMessage={errorMessage}
                                    getProposal={pagedData}
                                    handleApprove={handleApprove}
                                    handleReject={handleReject}
                                    handleView={handleView}
                                />
                            </table>
                        </div>
                    </div>
                </div>
            </div>
            {isShowViewConflictModal && <ProposalDetails/>}
            {isShowConfirmationModal && (
                <ConfirmDefaultModal
                    modalTitle={isApproveConflictProposal ? "Approve Existing Group Proposal" : "Reject Existing Group Proposal"}
                    modalSubTitle={isApproveConflictProposal ? "Are you sure you want to approve this proposal for an existing group with a different product?" : "Are you sure you want to reject this proposal for an existing group with a different product?"}
                    onCancel={() => dispatch(clearApproveRejectConflictProposal())}
                    onConfirm={() => handleApprovedRejectConflictProposal(conflictProposalId, actionType)}
                    isLoading={isLoadingApproveRejectProposalConflict}
                />
            )}
        </div>
    )
}